import { generatorIsOn, resolveControlMode } from "./generatorOutputs";

const NANOOX_CONTEXT_SCHEMA_VERSION = 1;
const MAX_CONTEXT_BYTES = 1 * 1024 * 1024; // 1 MB

function safeNum(v) {
  const n = parseFloat(v);
  return isFinite(n) ? Math.round(n * 100) / 100 : null;
}

function avg(values) {
  const valid = values.filter((v) => v !== null && v !== undefined && isFinite(v));
  if (!valid.length) return null;
  return safeNum(valid.reduce((a, b) => a + b, 0) / valid.length);
}

function getBombaStatus(bomba, umbrales) {
  if (!bomba.estado_comunicacion || bomba.estado_comunicacion === "off_line") {
    return "offline";
  }
  const o2 = safeNum(bomba.O2);
  if (o2 !== null && umbrales) {
    if (umbrales.O2_min !== undefined && o2 < umbrales.O2_min) return "alarm";
    if (umbrales.O2_max !== undefined && o2 > umbrales.O2_max) return "alarm";
  }
  return "ok";
}

function extractBombas(modulos) {
  const bombas = [];
  for (const modulo of modulos || []) {
    for (const can of modulo.cans || []) {
      for (const bomba of can.bombas || []) {
        bombas.push({ bomba, moduloId: modulo.id || modulo.nombre, moduloNombre: modulo.nombre || modulo.alias || "", canNombre: can.nombre || can.alias || "" });
      }
    }
  }
  return bombas;
}

function buildModulosSummary(modulos, umbrales) {
  return (modulos || []).map((modulo) => {
    const bombasFlat = (modulo.cans || []).flatMap((c) => c.bombas || []);
    const o2Values = bombasFlat.map((b) => safeNum(b.O2)).filter((v) => v !== null);
    const so2Values = bombasFlat.map((b) => safeNum(b.SO2)).filter((v) => v !== null);
    const tempValues = bombasFlat.map((b) => safeNum(b.TEMP)).filter((v) => v !== null);

    const statusCounts = { ok: 0, offline: 0, alarm: 0 };
    for (const b of bombasFlat) {
      statusCounts[getBombaStatus(b, umbrales)]++;
    }

    return {
      id: modulo.id || modulo.nombre,
      nombre: modulo.nombre || modulo.alias || "",
      canCount: (modulo.cans || []).length,
      bombaCount: bombasFlat.length,
      avgO2: avg(o2Values),
      avgSO2: avg(so2Values),
      avgTemp: avg(tempValues),
      statusBombas: statusCounts,
    };
  });
}

function buildBombasSummary(modulos, umbrales) {
  return extractBombas(modulos).map(({ bomba, moduloId, moduloNombre, canNombre }) => ({
    id: bomba.nombre || bomba.alias || "",
    nombre: bomba.nombre || bomba.alias || "",
    moduloId,
    moduloNombre,
    canNombre,
    O2: safeNum(bomba.O2),
    SO2: safeNum(bomba.SO2),
    TEMP: safeNum(bomba.TEMP),
    PRESION: safeNum(bomba.PRESION),
    SALINIDAD: safeNum(bomba.SALINIDAD),
    estado_comunicacion: bomba.estado_comunicacion || "unknown",
    modo_bomba: bomba.modo_bomba || null,
    status: getBombaStatus(bomba, umbrales),
  }));
}

function buildAlarmasActivas(modulos, umbrales) {
  if (!umbrales) return [];
  const alarmas = [];

  for (const { bomba, moduloNombre } of extractBombas(modulos)) {
    const o2 = safeNum(bomba.O2);
    if (o2 !== null) {
      if (umbrales.O2_min !== undefined && o2 < umbrales.O2_min) {
        alarmas.push({ bombaId: bomba.nombre, bombaNombre: `${moduloNombre} / ${bomba.nombre}`, tipo: "O2_BAJO", valor: o2, umbral: umbrales.O2_min });
      }
      if (umbrales.O2_max !== undefined && o2 > umbrales.O2_max) {
        alarmas.push({ bombaId: bomba.nombre, bombaNombre: `${moduloNombre} / ${bomba.nombre}`, tipo: "O2_ALTO", valor: o2, umbral: umbrales.O2_max });
      }
    }
    const temp = safeNum(bomba.TEMP);
    if (temp !== null) {
      if (umbrales.TEMP_min !== undefined && temp < umbrales.TEMP_min) {
        alarmas.push({ bombaId: bomba.nombre, bombaNombre: `${moduloNombre} / ${bomba.nombre}`, tipo: "TEMP_BAJA", valor: temp, umbral: umbrales.TEMP_min });
      }
      if (umbrales.TEMP_max !== undefined && temp > umbrales.TEMP_max) {
        alarmas.push({ bombaId: bomba.nombre, bombaNombre: `${moduloNombre} / ${bomba.nombre}`, tipo: "TEMP_ALTA", valor: temp, umbral: umbrales.TEMP_max });
      }
    }
  }

  return alarmas;
}

function buildGeneradoresSummary(generadores) {
  if (!Array.isArray(generadores) || !generadores.length) return [];

  return generadores.map((gen) => {
    const isTekFish = gen && typeof gen === "object" && "falla_generador_tekfish" in gen;

    if (isTekFish) {
      const commOk = Array.isArray(gen.connection_status)
        ? gen.connection_status.includes(true)
        : false;
      return {
        id: gen.id || gen.nombre || null,
        tipo: "tekfish",
        comunicacion: commOk ? "ok" : "sin_comunicacion",
        falla: Boolean(gen.falla_generador_tekfish),
        digital_outputs_state: gen.digital_outputs_state || null,
        estado: commOk
          ? gen.falla_generador_tekfish
            ? "alarma"
            : "operativo"
          : "sin_comunicacion",
      };
    }

    // Los generadores FSM caen en esta rama y no traen partida/detencion/
    // hf_status, asi que "apagado" nunca se activaba y las alarmas de bombas
    // no se suprimian nunca. generatorIsOn cubre ese caso.
    const isOn = generatorIsOn(gen);
    const { modo, reportado } = resolveControlMode(gen.control_generator_back);
    const apagado =
      (gen.partida === 60 && gen.detencion === 100) ||
      gen.hf_status === 60 ||
      isOn === false;

    return {
      id: gen.id || gen.nombre || null,
      tipo: "ps_vdf",
      partida: gen.partida ?? null,
      detencion: gen.detencion ?? null,
      hf_status: gen.hf_status ?? null,
      ON: gen.ON ?? null,
      STATE: gen.STATE ?? null,
      Control: gen.Control ?? null,
      digital_outputs_state: gen.digital_outputs_state ?? null,
      control_generator_back: gen.control_generator_back ?? null,
      control_generator_back_modo: modo,
      control_generator_back_reportado: reportado,
      estado: apagado ? "apagado" : "operativo",
    };
  });
}

function buildDashboardSummary(allCentersData) {
  if (!Array.isArray(allCentersData) || !allCentersData.length) return null;

  return allCentersData
    .filter((c) => c && c.modulos)
    .map((c) => {
      const bombasFlat = (c.modulos || []).flatMap((m) =>
        (m.cans || []).flatMap((can) => can.bombas || [])
      );
      const o2Values = bombasFlat.map((b) => safeNum(b.O2)).filter((v) => v !== null);
      const statusCounts = { ok: 0, offline: 0, alarm: 0 };
      for (const b of bombasFlat) {
        const umbrales = c.umbrales || {};
        statusCounts[getBombaStatus(b, umbrales)]++;
      }
      return {
        centro: c.centro || c.centerInfo?.centro || "",
        nombre: c.centerInfo?.nombre || c.centro || "",
        moduloCount: (c.modulos || []).length,
        bombaCount: bombasFlat.length,
        avgO2: avg(o2Values),
        statusBombas: statusCounts,
      };
    });
}

export function buildAssistantNanooxContext({
  centerData,
  selectedCenter,
  controlType,
  showDashboard,
  allCentersData,
  loading,
  generadores,
}) {
  if (loading || !selectedCenter) {
    return { schemaVersion: NANOOX_CONTEXT_SCHEMA_VERSION, state: "loading", source: "visible-ui" };
  }

  const modulos = centerData?.modulos;
  if (!modulos || !modulos.length) {
    return { schemaVersion: NANOOX_CONTEXT_SCHEMA_VERSION, state: "empty", source: "visible-ui" };
  }

  try {
    const umbrales = centerData?.umbrales || {};
    const modulosSummary = buildModulosSummary(modulos, umbrales);
    const bombasSummary = buildBombasSummary(modulos, umbrales);
    const alarmasActivas = buildAlarmasActivas(modulos, umbrales);

    const generadoresSummary = buildGeneradoresSummary(
      generadores ?? centerData?.generadores
    );

    const digest = {
      modulosSummary,
      bombasSummary,
      umbrales,
      alarmasActivas,
      generadoresSummary,
      centerInfo: {
        region: selectedCenter.region || "",
        area: selectedCenter.area || "",
        centro: selectedCenter.centro || "",
        controlType: controlType || null,
      },
    };

    if (showDashboard && allCentersData?.length) {
      digest.dashboardSummary = buildDashboardSummary(allCentersData);
    }

    const context = {
      schemaVersion: NANOOX_CONTEXT_SCHEMA_VERSION,
      state: "ready",
      source: "visible-ui",
      viewSignature: {
        service: "nanoox",
        module: showDashboard ? "dashboard" : "general",
        selectedCenter,
        controlType: controlType || null,
        showDashboard: !!showDashboard,
      },
      coverage: {
        visibleModuloCount: modulosSummary.length,
        visibleBombaCount: bombasSummary.length,
        generadoresCount: generadoresSummary.length,
        centersLoadedCount: showDashboard ? (allCentersData?.length || 0) : 1,
        alarmasCount: alarmasActivas.length,
        warnings: [],
      },
      digest,
    };

    const serialized = JSON.stringify(context);
    if (serialized.length > MAX_CONTEXT_BYTES) {
      digest.bombasSummary = bombasSummary.slice(0, 50);
      context.coverage.warnings.push("bombasSummary truncado a 50 entradas por tamaño");
    }

    return context;
  } catch (err) {
    return {
      schemaVersion: NANOOX_CONTEXT_SCHEMA_VERSION,
      state: "error",
      source: "visible-ui",
      error: err?.message || "Error desconocido",
    };
  }
}
